import { FC, useCallback, useContext, useEffect, useState } from 'react';
import { Menu, Box, useDisclosure, BoxProps, Flex, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { ShepherdTourContext } from 'react-shepherd';
import { CaretRight } from 'components/Icons/Icons';
import { RootState } from 'store';
import { hasAccessToCompanySettings } from 'utils/hasAccessToCompanySettings';
import { ReactComponent as LogOutIcon } from 'assets/svgs/logout.svg';
import ListItem from '../shared/ListItem';
import MenuButton from '../shared/MenuButton';
import ProfileCard from '../shared/ProfileCard';
import { DropdownMenuLink } from '../shared/StyledItems';
import { StyledMenuList, ListItemsWrapper } from '../shared/StyledItems';
import { logout } from 'modules/auth/authSlice';
import useActiveLink from 'hooks/useActiveLink';
import { switchProfileTo } from 'modules/user/userSlice';
import useBreakpoint from 'hooks/useBreakpoint';
import useRestartOnboarding from 'hooks/useRestartOnboarding';

interface Props extends BoxProps {
  items: MenuItem[];
  handleProfileType: (type: ProfileType) => void;
  profileType: ProfileType;
}

const highlightPadding = 4;

const MenuBox: FC<Props> = ({ items, handleProfileType, profileType, ...props }) => {
  const { currentAccount } = useSelector((state: RootState) => state.user);
  const context = useSelector((state: RootState) => state.app.context);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { activeLink, handleActiveLink } = useActiveLink(profileType);
  const dispatch = useDispatch();
  const { t } = useTranslation(['common', 'settings']);
  const tour = useContext(ShepherdTourContext);
  const { isDesktop } = useBreakpoint();
  const { restartOnboarding } = useRestartOnboarding();
  const [showTooltip, setShowTooltip] = useState(false);
  const isConnectContext = context === 'connect';
  const hasAccessToSettings = profileType === 'Company' ? hasAccessToCompanySettings(items.length) : true;
  const pictureUrl = profileType === 'Company' ? undefined : currentAccount?.profile?.pictureUrl;
  const name =
    profileType === 'Company'
      ? isConnectContext
        ? process.env.REACT_APP_PARTNER_NAME
        : currentAccount?.customer?.name
      : currentAccount?.profile?.name;

  const handleLogout = useCallback(() => {
    dispatch(logout());
  }, []);

  useEffect(() => {
    tour?.on('show', (options: unknown) => {
      if (
        // @ts-ignore
        (options.step.id === 'personal-settings' && profileType === 'Person') ||
        // @ts-ignore
        (options.step.id === 'company-settings' && profileType === 'Company')
      ) {
        onOpen();
      } else {
        onClose();
      }
      // @ts-ignore
      if (['analytics', 'team-members', 'metadata'].includes(options.step.id)) {
        dispatch(switchProfileTo('Company'));
      }
    });
  }, []);

  return (
    <Box {...props} position="relative">
      <Menu autoSelect={false} placement="bottom-end" isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
        <MenuButton
          rightIcon={hasAccessToSettings}
          showMenuList={hasAccessToSettings}
          isActive={!!activeLink}
          activeLinkHighlightPadding={highlightPadding}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          onClick={() => setShowTooltip(false)}
        >
          <ProfileCard profileType={profileType} name={name} pictureUrl={pictureUrl} showTooltip={showTooltip} />
        </MenuButton>
        {hasAccessToSettings && (
          <StyledMenuList
            w="350px"
            mr={activeLink ? `-${highlightPadding}px` : '0'}
            mt="12px"
            id={`${profileType.toLowerCase()}-menu`}
            className={`${profileType.toLowerCase()}-menu`}
          >
            {!!items.length && (
              <ListItemsWrapper as="ul" zIndex="333">
                {items.map(item => (
                  <ListItem as="li" key={`${profileType}_${item.id}`}>
                    <DropdownMenuLink
                      to={item.link}
                      isActive={(match: unknown) => handleActiveLink(item.text, match)}
                      onClick={() => {
                        handleProfileType(profileType);
                        onClose();
                      }}
                    >
                      {item.text}
                      <CaretRight />
                    </DropdownMenuLink>
                  </ListItem>
                ))}
              </ListItemsWrapper>
            )}
            {isDesktop && (
              <ListItemsWrapper mt="8px" py="11px">
                <Box as="button" textAlign="start" onClick={restartOnboarding}>
                  <Text lineHeight="20px">{t('routes.onboarding', { ns: 'settings' })}</Text>
                  <Text fontSize="xs" lineHeight="16px">
                    {t('navigateApp', { ns: 'settings' })}
                  </Text>
                </Box>
              </ListItemsWrapper>
            )}
            <ListItemsWrapper mt="8px">
              <Flex as="button" justifyContent="space-between" alignItems="center" w="100%" onClick={handleLogout}>
                <Text as="span" lineHeight="20px">
                  {t('menu.logout')}
                </Text>
                <LogOutIcon />
              </Flex>
            </ListItemsWrapper>
          </StyledMenuList>
        )}
      </Menu>
    </Box>
  );
};

export default MenuBox;
